import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Card,
    TextField,
    Typography,
    MenuItem,
    Button,
    Alert,
    CircularProgress,
    InputAdornment,
    IconButton,
    Link,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import { 
    Visibility, 
    VisibilityOff, 
    Email, 
    Lock, 
    Person,
    Phone 
} from "@mui/icons-material";
import { setVendorCredentials } from "../../features/admin/authSlice.ts";

const VendorLogin = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    const [currentView, setCurrentView] = useState("login");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [successMsg, setSuccessMsg] = useState("");
    const [loading, setLoading] = useState(false);

    const [loginEmail, setLoginEmail] = useState("");
    const [loginPassword, setLoginPassword] = useState("");

    const [regFirstName, setRegFirstName] = useState("");
    const [regLastName, setRegLastName] = useState("");
    const [regEmail, setRegEmail] = useState("");
    const [regContact, setRegContact] = useState("");
    const [regPassword, setRegPassword] = useState("");
    const [regConfirmPassword, setRegConfirmPassword] = useState("");

    // ── Company / vendor-verification details ──────────────────────────
    // Collected up front so admin has what they need to review the
    // application in Vendor Applications before approving product access.
    const [regBusinessName, setRegBusinessName] = useState("");
    const [regBusinessType, setRegBusinessType] = useState("shop");
    const [regCertificateName, setRegCertificateName] = useState("");
    const [regCompanyAddress, setRegCompanyAddress] = useState("");
    const [regCompanyLocation, setRegCompanyLocation] = useState("");
    // Pickup address collected during signup. Coordinates can be fetched automatically.
    const [regPickupName, setRegPickupName] = useState("");
    const [regPickupType, setRegPickupType] = useState("warehouse");
    const [regStreet, setRegStreet] = useState("");
    const [regFullPickupAddress, setRegFullPickupAddress] = useState("");
    const [regCity, setRegCity] = useState("");
    const [regState, setRegState] = useState("");
    const [regPostalCode, setRegPostalCode] = useState("");
    const [regLatitude, setRegLatitude] = useState("");
    const [regLongitude, setRegLongitude] = useState("");
    const [fetchingLocation, setFetchingLocation] = useState(false);
    const [regWebsite, setRegWebsite] = useState("");
    const [regCertificateFile, setRegCertificateFile] = useState(null);

    const [otpEmail, setOtpEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [showOtpInput, setShowOtpInput] = useState(false);

    const [resetEmail, setResetEmail] = useState("");
    const [resetOtp, setResetOtp] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");
    const [showResetOtpInput, setShowResetOtpInput] = useState(false);
    const [showPasswordFields, setShowPasswordFields] = useState(false);

    // Post-login pickup-address check. If the vendor already has an
    // address on file, we ask them to confirm it (or add a new one)
    // instead of silently reusing it — a vendor may have moved, or may
    // want a second pickup point. If they have none at all, we skip
    // straight to the "add address" dialog on their profile page.
    const [addressPromptOpen, setAddressPromptOpen] = useState(false);
    const [existingAddress, setExistingAddress] = useState<any>(null);
    const [addressCheckLoading, setAddressCheckLoading] = useState(false);

    const BASE_URL = (process.env.REACT_APP_SERVER_ENDPOINT || '').replace(/\/$/, '');
    // Backend mounts vendor authentication under /user.
    // Keep this safe if the environment already contains /user.
    const USER_API_URL = BASE_URL.endsWith('/user') ? BASE_URL : `${BASE_URL}/user`;
    const AUTH_KEY = "7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3";


    const fetchSignupLocation = () => {
        if (!navigator.geolocation) { setErrorMsg("Location is not supported by this browser"); return; }
        setFetchingLocation(true); setErrorMsg("");
        navigator.geolocation.getCurrentPosition(async (position) => {
            const { latitude, longitude } = position.coords;
            setRegLatitude(String(latitude)); setRegLongitude(String(longitude));
            try {
                const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`);
                const data = await response.json();
                const a = data?.address || {};
                const fullAddress = data?.display_name || "";
                setRegStreet([a.house_number, a.road].filter(Boolean).join(" "));
                setRegFullPickupAddress(fullAddress);
                setRegCity(a.city || a.town || a.village || a.county || "");
                setRegState(a.state || "");
                setRegPostalCode(a.postcode || "");
                setRegCompanyLocation(a.city || a.town || a.village || a.county || "");
                setRegCompanyAddress(fullAddress);
            } catch (_) { /* coordinates were still captured */ }
            finally { setFetchingLocation(false); }
        }, () => { setFetchingLocation(false); setErrorMsg("Unable to fetch location. Please allow location permission and try again."); }, { enableHighAccuracy: true, timeout: 15000 });
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhone = (phone) => {
        const phoneRegex = /^[0-9]{10}$/;
        return phoneRegex.test(phone);
    };

    const validateVendorPassword = (password) => {
        return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/.test(password);
    };
const checkVendorAddressAndProceed = async (token) => {
    setAddressCheckLoading(true);
    try {
        const response = await fetch(`${BASE_URL}/product/warehouselist`, {
            method: "POST",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
                "Accept": "application/json",
                "authkey": AUTH_KEY,
                Authorization: `Bearer ${token}`,
            },
            body: new URLSearchParams(),
        });
        const result = await response.json();
        const list = result?.warehouses || result?.result || [];

        if (!Array.isArray(list) || list.length === 0) {
            // No pickup address at all yet — go straight to adding one.
            navigate("/adminProfile", {
                state: {
                    openAddressDialog: true,
                    addressPrompt: "Welcome! Please add your pickup address (warehouse, shop, or home) before adding products.",
                },
            });
            return;
        }

        const defaultAddress = list.find((w) => Number(w.is_default) === 1) || list[0];
        setExistingAddress(defaultAddress);
        setAddressPromptOpen(true);
    } catch (error) {
        // Don't block login over a failed address check — let them in and
        // they can manage addresses from their profile any time.
        navigate("/productManagement");
    } finally {
        setAddressCheckLoading(false);
    }
};

const handleUseExistingAddress = () => {
    setAddressPromptOpen(false);
    navigate("/productManagement");
};

const handleAddNewAddress = () => {
    setAddressPromptOpen(false);
    navigate("/adminProfile", {
        state: {
            openAddressDialog: true,
            addressPrompt: "Add a new pickup address, or edit/remove your existing ones below.",
        },
    });
};

const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    if (!loginEmail.trim()) {
        setErrorMsg("Email is required");
        setLoading(false);
        return;
    }

    if (!validateEmail(loginEmail)) {
        setErrorMsg("Please enter a valid email address");
        setLoading(false);
        return;
    }

    if (!loginPassword.trim()) {
        setErrorMsg("Password is required");
        setLoading(false);
        return;
    }

    try {
        const response = await fetch(`${USER_API_URL}/vendorlogin`, {
            method: "POST",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
                "Accept": "application/json",
                "authkey": AUTH_KEY,
            },
            body: new URLSearchParams({
                user_email: loginEmail.trim(),
                user_pass: loginPassword,
            }),
        });

        const result = await response.json();

        const payload = result?.data && typeof result.data === "object" ? result.data : result;

        if (!result.error && payload.token) {
            // Debug: Log the entire response
            console.log("=== FULL RESPONSE ===");
            console.log(JSON.stringify(result, null, 2));
            console.log("Result object:", result);
            console.log("Type of result:", typeof result);
            console.log("Is array?:", Array.isArray(result));
            if (result.data) console.log("result.data:", result.data);
            if (result.user) console.log("result.user:", result.user);
            console.log("=====================");
            
            // Extract vendor_id - try multiple possible locations
            let vendorId = payload.vendor_id || payload.vendorId || payload.VendorId || payload.id;
            
            // If not found at root level, check other possible locations
            if (!vendorId) {
                vendorId = payload.vendor_id || payload.VendorId || payload.vendorId || payload.id;
            }
            
            const userName = payload.user_name || payload.userName || payload.name || `Vendor User`;

            // Allow login even if vendorId is missing (will use as fallback)
            if (!vendorId) {
                console.warn("Warning: vendor_id not found in response");
                // Don't fail here - use a temporary ID and let backend handle it
                setErrorMsg("Vendor ID was not returned by the server. Please contact the administrator.");
                setLoading(false);
                return;
            }

            // Clear any existing admin session completely first. If a
            // Super Admin/Admin/Operation session logged in earlier on this
            // browser, its role/permission keys (admin_type, role,
            // admin_permissions, admin_perm_map, admin_role_id, etc.) stay
            // in localStorage unless removed here. Both AdminHeader and
            // AdminSlideMenu read `admin_type` / `role` BEFORE `user_role`,
            // so a leftover "super_admin" would make a vendor session show
            // "Super Admin" in the header and grant it full menu access
            // (Order Management, Return, User List, ...) instead of being
            // restricted to Product Management.
            localStorage.removeItem("admin_token");
            localStorage.removeItem("admin_password");
            localStorage.removeItem("admin_type");
            localStorage.removeItem("role");
            localStorage.removeItem("admin_id");
            localStorage.removeItem("admin_name");
            localStorage.removeItem("admin_email");
            localStorage.removeItem("admin_role_id");
            localStorage.removeItem("admin_permissions");
            localStorage.removeItem("admin_perm_map");
            localStorage.removeItem("user_role");

            // Store vendor credentials in localStorage
            localStorage.setItem("vendor_token", payload.token);
            localStorage.setItem("user_role", "vendor");
            localStorage.setItem("user_email", loginEmail.trim());
            localStorage.setItem("vendor_id", vendorId.toString());
            localStorage.setItem("user_name", userName);

            // Save role-based permissions (from the "Vendor" role in Role
            // Master → Role Mapping, resolved server-side in /vendorlogin).
            // adminSlidemenu.tsx / PermissionContext.tsx / App.tsx's
            // ProtectedRoute all read `admin_permissions` + `admin_role_id`
            // the same way they do for admin logins, so whatever an admin
            // grants the "Vendor" role opens up automatically here.
            if (payload.permissions && Object.keys(payload.permissions).length > 0) {
                localStorage.setItem("admin_permissions", JSON.stringify(payload.permissions));
            } else {
                localStorage.removeItem("admin_permissions");
            }
            if (payload.role_id) {
                localStorage.setItem("admin_role_id", String(payload.role_id));
            } else {
                localStorage.removeItem("admin_role_id");
            }
            localStorage.removeItem("admin_perm_map"); // force SlideMenu to rebuild fresh

            // Dispatch Redux action to store credentials
            dispatch(setVendorCredentials({
                token: payload.token,
                email: loginEmail.trim(),
                vendorId: vendorId,
                userName: userName,
                role: "vendor"
            }));

            // Company-verification gate: a seller who applied but hasn't
            // been approved by an admin yet (see Vendor Applications in the
            // back office) can log in to see their status, but shouldn't be
            // dropped into Product Management — every product API call
            // would just 403. Surface that clearly instead.
            localStorage.setItem("vendor_approval_status", payload.approval_status || "pending");
            if (payload.approval_status && payload.approval_status !== "approved") {
                setLoading(false);
                if (payload.approval_status === "rejected") {
                    setErrorMsg("Your seller application was not approved. Please contact support for details.");
                } else {
                    setSuccessMsg("Login successful. Your seller application is still under review — product uploads will unlock once an admin approves it.");
                }
                return;
            }

            setSuccessMsg("Login successful!");

            // Before landing on Product Management, make sure this vendor
            // has a pickup address on file — either confirm the one they
            // already have, or send them to add one. Without a pickup
            // address, adding a product fails server-side.
            setTimeout(() => {
                checkVendorAddressAndProceed(payload.token);
            }, 800);
        } else {
            setErrorMsg(result.message || "Invalid credentials");
        }
    } catch (error) {
        setErrorMsg("Network error: Unable to connect to server");
    } finally {
        setLoading(false);
    } 
};
    const handleRegistration = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMsg("");
        setSuccessMsg("");

        if (!regFirstName.trim()) {
            setErrorMsg("First name is required");
            setLoading(false);
            return;
        }

        if (!regLastName.trim()) {
            setErrorMsg("Last name is required");
            setLoading(false);
            return;
        }

        if (!validateEmail(regEmail)) {
            setErrorMsg("Please enter a valid email address");
            setLoading(false);
            return;
        }

        if (!validatePhone(regContact)) {
            setErrorMsg("Please enter a valid 10-digit contact number");
            setLoading(false);
            return;

        }

        if (!validateVendorPassword(regPassword)) {
            setErrorMsg("Password must be at least 8 characters and include uppercase, lowercase, number and special character");
            setLoading(false);
            return;
        }

        if (regPassword !== regConfirmPassword) {
            setErrorMsg("Passwords do not match");
            setLoading(false);
            return;
        }

        if (!regBusinessName.trim()) {
            setErrorMsg("Company / business name is required");
            setLoading(false);
            return;
        }

        if (!regPickupName.trim() || !regFullPickupAddress.trim() || !regLatitude || !regLongitude) {
            setErrorMsg("Please complete the pickup address and fetch your current location");
            setLoading(false);
            return;
        }

        if (!regCertificateName.trim()) {
            setErrorMsg("Please enter the certificate name or license type");
            setLoading(false);
            return;
        }

        if (!regCertificateFile) {
            setErrorMsg("Please attach your business / premises certificate (PDF or image)");
            setLoading(false);
            return;
        }

        try {
            // multipart/form-data (not urlencoded) so the registration
            // certificate file can go up in the same request.
            const formData = new FormData();
            formData.append("first_name", regFirstName.trim());
            formData.append("last_name", regLastName.trim());
            formData.append("user_email", regEmail.trim());
            formData.append("user_contact", regContact.trim());
            formData.append("user_pass", regPassword);
            formData.append("confirm_password", regConfirmPassword);
            formData.append("business_name", regBusinessName.trim());
            formData.append("business_type", regBusinessType);
            formData.append("certificate_name", regCertificateName.trim());
            formData.append("company_address", regFullPickupAddress.trim() || regCompanyAddress.trim() || [regStreet, regCity, regState, regPostalCode].filter(Boolean).join(", "));
            formData.append("pickup_address", regFullPickupAddress.trim());
            formData.append("company_location", regCompanyLocation.trim() || regCity.trim());
            formData.append("pickup_name", regPickupName.trim());
            formData.append("pickup_type", regPickupType);
            formData.append("pickup_street", regStreet.trim());
            formData.append("pickup_city", regCity.trim());
            formData.append("pickup_state", regState.trim());
            formData.append("pickup_postal_code", regPostalCode.trim());
            formData.append("pickup_latitude", regLatitude);
            formData.append("pickup_longitude", regLongitude);
            formData.append("website", regWebsite.trim());
            formData.append("registration_certificate", regCertificateFile);

            const response = await fetch(`${USER_API_URL}/vendorregister`, {
                method: "POST",
                headers: {
                    "Accept": "application/json",
                    "authkey": AUTH_KEY,
                    // No Content-Type header — the browser sets the correct
                    // multipart boundary automatically for FormData.
                },
                body: formData,
            });

            const result = await response.json();
            const payload = result?.data && typeof result.data === "object" ? result.data : result;

            if (!result.error) {
                setSuccessMsg(payload.message || result.message || "Thanks for registering. Please verify your email with OTP. Your personal details, address and certificate have been submitted for admin review. Please wait for approval before product uploads are enabled.");
                setOtpEmail(regEmail);
                setShowOtpInput(true);
                setRegFirstName("");
                setRegLastName("");
                setRegEmail("");
                setRegContact("");
                setRegPassword("");
                setRegConfirmPassword("");
                setRegBusinessName("");
                setRegBusinessType("shop");
                setRegCertificateName("");
                setRegCompanyAddress("");
                setRegFullPickupAddress("");
                setRegCompanyLocation("");
                setRegWebsite("");
                setRegCertificateFile(null);
            } else {
                setErrorMsg(result.message || "Registration failed");
            }
        } catch (error) {
            setErrorMsg("Network error: Unable to connect to server");
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMsg("");
        setSuccessMsg("");

        if (!otp.trim()) {
            setErrorMsg("OTP is required");
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${USER_API_URL}/vendorverify-otp`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "Accept": "application/json",
                    "authkey": AUTH_KEY,
                },
                body: new URLSearchParams({
                    user_email: otpEmail,
                    otp: otp,
                }),
            });

            const result = await response.json();

            if (!result.error) {
                setSuccessMsg("Thanks for waiting. Your OTP is verified successfully. Your application, personal details, address and certificate are now under admin review. Please wait until the admin approves your account; product uploads will be enabled after approval.");
                setTimeout(() => {
                    setShowOtpInput(false);
                    setOtp("");
                    setCurrentView("login");
                }, 2000);
            } else {
                setErrorMsg(result.message || "Invalid OTP");
            }
        } catch (error) {
            setErrorMsg("Network error: Unable to connect to server");
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMsg("");
        setSuccessMsg("");

        if (!validateEmail(resetEmail)) {
            setErrorMsg("Please enter a valid email address");
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${USER_API_URL}/vendorforgot-password`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "Accept": "application/json",
                    "authkey": AUTH_KEY,
                },
                body: new URLSearchParams({
                    user_email: resetEmail.trim(),
                }),
            });

            const result = await response.json();

            if (!result.error) {
                setSuccessMsg(result.message || "OTP sent to your email");
                setShowResetOtpInput(true);
            } else {
                setErrorMsg(result.message || "Failed to send OTP");
            }
        } catch (error) {
            setErrorMsg("Network error: Unable to connect to server");
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMsg("");
        setSuccessMsg("");

        if (!resetOtp.trim()) {
            setErrorMsg("OTP is required");
            setLoading(false);
            return;
        }

        if (!showPasswordFields) {
            setShowPasswordFields(true);
            setLoading(false);
            return;
        }

        if (!validateVendorPassword(newPassword)) {
            setErrorMsg("Password must be at least 8 characters and include uppercase, lowercase, number and special character");
            setLoading(false);
            return;
        }

        if (newPassword !== confirmNewPassword) {
            setErrorMsg("Passwords do not match");
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${USER_API_URL}/vendorreset-password`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "Accept": "application/json",
                    "authkey": AUTH_KEY,
                },
                body: new URLSearchParams({
                    user_email: resetEmail.trim(),
                    otp: resetOtp,
                    new_password: newPassword,
                    confirm_password: confirmNewPassword,
                }),
            });

            const result = await response.json();

            if (!result.error) {
                setSuccessMsg(result.message || "Password reset successful!");
                setTimeout(() => {
                    setResetEmail("");
                    setResetOtp("");
                    setNewPassword("");
                    setConfirmNewPassword("");
                    setShowResetOtpInput(false);
                    setShowPasswordFields(false);
                    setCurrentView("login");
                }, 2000);
            } else {
                setErrorMsg(result.message || "Password reset failed");
            }
        } catch (error) {
            setErrorMsg("Network error: Unable to connect to server");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                padding: 2,
            }}
        >
            <Card
                sx={{
                    width: { xs: '100%', sm: 620 },
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    padding: 4,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                    borderRadius: 3,
                    backgroundColor: "#ffffff",
                }}
            >
                <Typography
                    variant="h4"
                    align="center"
                    gutterBottom
                    sx={{
                        fontWeight: "bold",
                        mb: 1,
                        color: "#333",
                    }}
                >
                    {currentView === "login" && "Vendor Login"}
                    {currentView === "register" && (showOtpInput ? "Verify OTP" : "Sign Up")}
                    {currentView === "forgot" && "Forgot Password"}
                </Typography>

                <Typography
                    variant="body2"
                    align="center"
                    sx={{ mb: 3, color: "#666" }}
                >
                    {currentView === "login" && ""}
                    {currentView === "register" && (showOtpInput ? "Verify your email address" : "Create your vendor account")}
                    {currentView === "forgot" && "Reset your password"}
                </Typography>

                {successMsg && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {successMsg}
                    </Alert>
                )}

                {errorMsg && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {errorMsg}
                    </Alert>
                )}

                {currentView === "login" && (
                    <form onSubmit={handleLogin}>
                        <TextField
                            placeholder="Type your username"
                            type="email"
                            fullWidth
                            value={loginEmail}
                            onChange={(e) => setLoginEmail(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Person sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            placeholder="Type your password"
                            type={showPassword ? "text" : "password"}
                            fullWidth
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Box sx={{ textAlign: "right", mb: 3 }}>
                            <Link
                                component="button"
                                type="button"
                                variant="body2"
                                onClick={() => setCurrentView("forgot")}
                                sx={{ 
                                    textDecoration: "none",
                                    color: "#667eea",
                                    "&:hover": { textDecoration: "underline" }
                                }}
                            >
                                Forgot password?
                            </Link>
                        </Box>

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            sx={{
                                py: 1.5,
                                fontSize: "1rem",
                                textTransform: "none",
                                background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
                                borderRadius: 25,
                                mb: 3,
                                "&:hover": {
                                    background: "linear-gradient(90deg, #5568d3 0%, #653a8b 100%)",
                                },
                            }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                                    Signing In..
                                </>
                            ) : (
                                "LOGIN"
                            )}
                        </Button>

                        <Box sx={{ textAlign: "center" }}>
                            <Typography variant="body2" sx={{ color: "#666" }}>
                                Don't have an account?{" "}
                                <Link
                                    component="button"
                                    type="button"
                                    onClick={() => setCurrentView("register")}
                                    sx={{ 
                                        textDecoration: "none",
                                        color: "#667eea",
                                        fontWeight: 600,
                                        "&:hover": { textDecoration: "underline" }
                                    }}
                                >
                                    SIGN UP
                                </Link>
                            </Typography>
                        </Box>
                    </form>
                )}

                {currentView === "register" && !showOtpInput && (
                    <form onSubmit={handleRegistration}>
                        <TextField
                            placeholder="First Name"
                            fullWidth
                            value={regFirstName}
                            onChange={(e) => setRegFirstName(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Person sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            placeholder="Last Name"
                            fullWidth
                            value={regLastName}
                            onChange={(e) => setRegLastName(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Person sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            placeholder="Email Address"
                            type="email"
                            fullWidth
                            value={regEmail}
                            onChange={(e) => setRegEmail(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Email sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            placeholder="Contact Number"
                            fullWidth
                            value={regContact}
                            onChange={(e) => setRegContact(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Phone sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            placeholder="Password"
                            type={showPassword ? "text" : "password"}
                            fullWidth
                            value={regPassword}
                            onChange={(e) => setRegPassword(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            placeholder="Confirm Password"
                            type={showConfirmPassword ? "text" : "password"}
                            fullWidth
                            value={regConfirmPassword}
                            onChange={(e) => setRegConfirmPassword(e.target.value)}
                            sx={{ mb: 3 }}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Typography variant="subtitle2" sx={{ color: "#555", fontWeight: 700, mt: 1, mb: 1.5 }}>
                            Company details
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#888", display: "block", mb: 2 }}>
                            We use this to verify your business before approving product uploads.
                        </Typography>

                        <TextField
                            placeholder="Company / Business Name"
                            fullWidth
                            value={regBusinessName}
                            onChange={(e) => setRegBusinessName(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                        />

                        <TextField
                            select
                            label="Business Premises Type"
                            fullWidth
                            value={regBusinessType}
                            onChange={(e) => setRegBusinessType(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                        >
                            <MenuItem value="shop">Shop / Retail Store</MenuItem>
                            <MenuItem value="warehouse">Warehouse / Storage</MenuItem>
                            <MenuItem value="both">Shop and Warehouse</MenuItem>
                            <MenuItem value="office">Office / Commercial Premises</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                        </TextField>

                        <TextField
                            label="Certificate Name / License Type"
                            placeholder="e.g. Trade License, Shop & Establishment License, Warehouse License"
                            fullWidth
                            value={regCertificateName}
                            onChange={(e) => setRegCertificateName(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            helperText="Enter the name or type of the certificate you are uploading"
                        />

                        <Typography variant="subtitle2" sx={{ color: "#555", fontWeight: 700, mt: 1, mb: 1.5 }}>
                            Pickup Address
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#888", display: "block", mb: 1.5 }}>
                            This address will be saved to your profile and shown to the admin for approval.
                        </Typography>
                        <TextField label="Pickup Address Name" placeholder="e.g. Main Shop / Warehouse" fullWidth value={regPickupName} onChange={(e) => setRegPickupName(e.target.value)} sx={{ mb: 2 }} disabled={loading} />
                        <TextField select label="Pickup Type" fullWidth value={regPickupType} onChange={(e) => setRegPickupType(e.target.value)} sx={{ mb: 2 }} disabled={loading}>
                            <MenuItem value="warehouse">Warehouse</MenuItem><MenuItem value="shop">Shop</MenuItem><MenuItem value="home">Home</MenuItem>
                        </TextField>
                        <Button variant="outlined" fullWidth onClick={fetchSignupLocation} disabled={loading || fetchingLocation} sx={{ mb: 2, textTransform: "none" }}>
                            {fetchingLocation ? "Fetching current location..." : "Use My Current Location (Auto Fetch)"}
                        </Button>
                        <TextField
                            label="Full Pickup Address"
                            placeholder="Click Use My Current Location to auto-fill this address"
                            fullWidth
                            multiline
                            minRows={2}
                            value={regFullPickupAddress}
                            onChange={(e) => setRegFullPickupAddress(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            helperText="This address is saved to the vendor profile and shown to the admin."
                        />
                        <TextField label="Street / Area" fullWidth value={regStreet} onChange={(e) => setRegStreet(e.target.value)} sx={{ mb: 2 }} disabled={loading} />
                        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                            <TextField label="City" fullWidth value={regCity} onChange={(e) => setRegCity(e.target.value)} disabled={loading} />
                            <TextField label="State" fullWidth value={regState} onChange={(e) => setRegState(e.target.value)} disabled={loading} />
                        </Box>
                        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                            <TextField label="Postal Code" fullWidth value={regPostalCode} onChange={(e) => setRegPostalCode(e.target.value)} disabled={loading} />
                            <TextField label="Latitude" fullWidth value={regLatitude} onChange={(e) => setRegLatitude(e.target.value)} disabled={loading} />
                            <TextField label="Longitude" fullWidth value={regLongitude} onChange={(e) => setRegLongitude(e.target.value)} disabled={loading} />
                        </Box>
                        <TextField
                            placeholder="Company Website (optional)"
                            fullWidth
                            value={regWebsite}
                            onChange={(e) => setRegWebsite(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                        />

                        <Button
                            variant="outlined"
                            component="label"
                            fullWidth
                            disabled={loading}
                            sx={{ mb: 1, py: 1.2, textTransform: "none", borderRadius: 2, borderColor: "#c9cccf", color: "#444" }}
                        >
                            {regCertificateFile ? regCertificateFile.name : "Upload Business / Shop / Warehouse Certificate"}
                            <input
                                type="file"
                                hidden
                                accept=".pdf,image/*"
                                onChange={(e) => setRegCertificateFile(e.target.files?.[0] || null)}
                            />
                        </Button>
                        <Typography variant="caption" sx={{ color: "#888", display: "block", mb: 3 }}>
                            PDF or image. Examples: Shop & Establishment License, Trade License, Warehouse License, Commercial Registration Certificate.
                        </Typography>

                        <Alert severity="info" sx={{ mb: 3 }}>
                            After sign-up you'll verify your email with an OTP. Product uploads unlock once our team reviews and approves your application — we'll email you either way.
                        </Alert>

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            sx={{
                                py: 1.5,
                                fontSize: "1rem",
                                textTransform: "none",
                                background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
                                borderRadius: 25,
                                mb: 3,
                                "&:hover": {
                                    background: "linear-gradient(90deg, #5568d3 0%, #653a8b 100%)",
                                },
                            }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                                    Registering...
                                </>
                            ) : (
                                "SUBMIT APPLICATION"
                            )}
                        </Button>

                        <Box sx={{ textAlign: "center" }}>
                            <Typography variant="body2" sx={{ color: "#666" }}>
                                Already have an account?{" "}
                                <Link
                                    component="button"
                                    type="button"
                                    onClick={() => setCurrentView("login")}
                                    sx={{ 
                                        textDecoration: "none",
                                        color: "#667eea",
                                        fontWeight: 600,
                                        "&:hover": { textDecoration: "underline" }
                                    }}
                                >
                                    LOGIN
                                </Link>
                            </Typography>
                        </Box>
                    </form>
                )}

                {currentView === "register" && showOtpInput && (
                    <form onSubmit={handleVerifyOtp}>
                        <Typography variant="body2" sx={{ mb: 2, color: "#666", textAlign: "center" }}>
                            An OTP has been sent to {otpEmail}
                        </Typography>

                        <TextField
                            placeholder="Enter OTP"
                            fullWidth
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                            sx={{ mb: 3 }}
                            disabled={loading}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            sx={{
                                py: 1.5,
                                fontSize: "1rem",
                                textTransform: "none",
                                background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
                                borderRadius: 25,
                                mb: 3,
                                "&:hover": {
                                    background: "linear-gradient(90deg, #5568d3 0%, #653a8b 100%)",
                                },
                            }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                                    Verifying...
                                </>
                            ) : (
                                "Verify OTP"
                            )}
                        </Button>

                        <Box sx={{ textAlign: "center" }}>
                            <Link
                                component="button"
                                type="button"
                                onClick={() => {
                                    setShowOtpInput(false);
                                    setCurrentView("login");
                                }}
                                sx={{ 
                                    textDecoration: "none",
                                    color: "#667eea",
                                    "&:hover": { textDecoration: "underline" }
                                }}
                            >
                                Back to Login
                            </Link>
                        </Box>
                    </form>
                )}

                {currentView === "forgot" && !showResetOtpInput && (
                    <form onSubmit={handleForgotPassword}>
                        <TextField
                            placeholder="Enter your email address"
                            type="email"
                            fullWidth
                            value={resetEmail}
                            onChange={(e) => setResetEmail(e.target.value)}
                            sx={{ mb: 3 }}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Email sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            sx={{
                                py: 1.5,
                                fontSize: "1rem",
                                textTransform: "none",
                                background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
                                borderRadius: 25,
                                mb: 3,
                                "&:hover": {
                                    background: "linear-gradient(90deg, #5568d3 0%, #653a8b 100%)",
                                },
                            }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                                    Sending OTP...
                                </>
                            ) : (
                                "Send OTP"
                            )}
                        </Button>

                        <Box sx={{ textAlign: "center" }}>
                            <Link
                                component="button"
                                type="button"
                                onClick={() => setCurrentView("login")}
                                sx={{ 
                                    textDecoration: "none",
                                    color: "#667eea",
                                    "&:hover": { textDecoration: "underline" }
                                }}
                            >
                                Back to Login
                            </Link>
                        </Box>
                    </form>
                )}

                {currentView === "forgot" && showResetOtpInput && (
                    <form onSubmit={handleResetPassword}>
                        <Typography variant="body2" sx={{ mb: 2, color: "#666", textAlign: "center" }}>
                            Enter the OTP sent to {resetEmail}
                        </Typography>

                        <TextField
                            placeholder="Enter OTP"
                            fullWidth
                            value={resetOtp}
                            onChange={(e) => setResetOtp(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                        />

                        {showPasswordFields && (
                            <>
                                <TextField
                                    placeholder="New Password"
                                    type={showPassword ? "text" : "password"}
                                    fullWidth
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    sx={{ mb: 2 }}
                                    disabled={loading}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock sx={{ color: "#999" }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    placeholder="Confirm New Password"
                                    type={showConfirmPassword ? "text" : "password"}
                                    fullWidth
                                    value={confirmNewPassword}
                                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                                    sx={{ mb: 3 }}
                                    disabled={loading}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock sx={{ color: "#999" }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    edge="end"
                                                >
                                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </>
                        )}

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            sx={{
                                py: 1.5,
                                fontSize: "1rem",
                                textTransform: "none",
                                background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
                                borderRadius: 25,
                                mb: 3,
                                "&:hover": {
                                    background: "linear-gradient(90deg, #5568d3 0%, #653a8b 100%)",
                                },
                            }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                                    {showPasswordFields ? "Resetting..." : "Verifying..."}
                                </>
                            ) : (
                                showPasswordFields ? "Reset Password" : "Verify OTP"
                            )}
                        </Button>

                        <Box sx={{ textAlign: "center" }}>
                            <Link
                                component="button"
                                type="button"
                                onClick={() => {
                                    setShowResetOtpInput(false);
                                    setShowPasswordFields(false);
                                    setCurrentView("login");
                                }}
                                sx={{ 
                                    textDecoration: "none",
                                    color: "#667eea",
                                    "&:hover": { textDecoration: "underline" }
                                }}
                            >
                                Back to Login
                            </Link>
                        </Box>
                    </form>
                )}
            </Card>

            {/* Post-login pickup address confirmation */}
            <Dialog open={addressPromptOpen} maxWidth="xs" fullWidth>
                <DialogTitle>Confirm Pickup Address</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        We have this pickup address on file for your account:
                    </Typography>
                    {existingAddress && (
                        <Box sx={{ p: 2, borderRadius: 2, bgcolor: "#f5f5f5", mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {existingAddress.name}
                            </Typography>
                            {existingAddress.address && (
                                <Typography variant="body2" color="text.secondary">
                                    {existingAddress.address}
                                </Typography>
                            )}
                        </Box>
                    )}
                    <Typography variant="body2" color="text.secondary">
                        Use this address, or add a new one for this login.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleAddNewAddress} sx={{ textTransform: "none" }}>
                        Add New Address
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleUseExistingAddress}
                        sx={{ textTransform: "none", background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)" }}
                    >
                        Use This Address
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default VendorLogin;